import { safeLocalStorage } from '../../utils/safeLocalStorage';

// v5 chat/daily-plan/weekly-plan generation runs server-side and can be resumed after a
// reload via GET /api/v1/jobs/{job_id} — but only if the job_id survives the
// reload. recoil-persist/component state don't cover this (they're gone or
// reset on remount), so job records get their own localStorage slot, written
// synchronously before the generation request is sent.
export type RecoverableJobType = 'chat' | 'daily_plan' | 'weekly_plan';

export interface PersistedJobRecord<TContext> {
  jobId: string;
  jobType: RecoverableJobType;
  createdAt: number;
  context: TContext;
}

/**
 * Request-body fields that tell the server which job id the client will poll later.
 * The v9 plan route reads camelCase `jobId` while the chat route reads `job_id`.
 * Sending both keeps recovery working on either. If the server never receives the
 * id it mints its own, and polling the client's id then 404s so a refresh cannot
 * resume the run.
 */
export const jobIdBodyFields = (jobId?: string): { jobId?: string; job_id?: string } =>
  jobId ? { jobId, job_id: jobId } : {};

const storageKey = (jobType: RecoverableJobType): string => `lifestack_pending_job_${jobType}`;

export function getPersistedJob<TContext>(
  jobType: RecoverableJobType
): PersistedJobRecord<TContext> | null {
  const raw = safeLocalStorage.getItem(storageKey(jobType));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PersistedJobRecord<TContext>;
    if (!parsed?.jobId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setPersistedJob<TContext>(
  jobType: RecoverableJobType,
  jobId: string,
  context: TContext
): void {
  const record: PersistedJobRecord<TContext> = {
    jobId,
    jobType,
    createdAt: Date.now(),
    context,
  };
  safeLocalStorage.setItem(storageKey(jobType), JSON.stringify(record));
}

export function clearPersistedJob(jobType: RecoverableJobType): void {
  safeLocalStorage.removeItem(storageKey(jobType));
}

/** Only clear if the persisted record still points at this exact job — avoids
 * a finishing/erroring stream clobbering a newer job that superseded it. */
export function clearPersistedJobIfMatches(jobType: RecoverableJobType, jobId: string): void {
  const current = getPersistedJob(jobType);
  if (current?.jobId === jobId) {
    clearPersistedJob(jobType);
  }
}

const IN_FLIGHT_DISCONNECT_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
  'Network request failed',
  // Chrome / Safari wording when a reload cuts off an in-flight response body.
  'network error',
  'The network connection was lost.',
  'Stream aborted',
  'Stream closed',
]);

/** Refresh cancels the local fetch while science keeps the job. Those errors
 * must leave the persisted id and the hidden cache alone. Other TypeErrors
 * are real failures and must not be treated as a disconnect. */
export function isInFlightPlanDisconnect(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.name === 'AbortError' || IN_FLIGHT_DISCONNECT_MESSAGES.has(error.message);
}
